import {
  Bell,
  Calendar,
  CalendarPlus,
  CalendarX,
  CalendarClock,
  FileText,
  Route,
  Zap,
  CheckCircle,
  UserPlus,
  Clock,
  Settings,
  Phone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Notification } from '../../types/notification';

/**
 * ============================================================================
 * Shared notification config
 * ============================================================================
 * Single source of truth for notification styling, labels, categories and
 * deep-link building. Previously duplicated across NotificationDropdown and
 * NotificationsPage — now imported by both.
 * ============================================================================
 */

export type NotificationCategory = 'all' | 'routemize' | 'requests' | 'appointments' | 'messages' | 'system';

export interface NotificationVisualConfig {
  icon: LucideIcon;
  /** Tailwind class for the icon tile background. */
  tileBg: string;
  /** Tailwind class for the icon color. */
  tileIcon: string;
  /** Tailwind class for the small inline pill label. */
  pillBg: string;
  pillText: string;
  /** Human-readable label shown on the pill. */
  label: string;
  /** When true, treat as a "hero" event worth celebrating (Routemize bookings). */
  highlight?: boolean;
  /** Which top-level category this type belongs to, used for the filter pills. */
  category: Exclude<NotificationCategory, 'all'>;
}

const apptBg = 'bg-sky-50';
const apptIcon = 'text-sky-600';
const apptPillBg = 'bg-sky-50';
const apptPillText = 'text-sky-700';

/**
 * Map of every known notification type string -> visual config.
 * Keys are lowercase and snake/camelCase variants are both supported because
 * the backend has historically been inconsistent.
 */
const CONFIG_BY_TYPE: Record<string, NotificationVisualConfig> = {
  // Routemize public booking — hero event
  routemize_appointment: {
    icon: Route,
    tileBg: 'bg-gradient-to-br from-amber-100 to-yellow-50',
    tileIcon: 'text-amber-600',
    pillBg: 'bg-amber-100',
    pillText: 'text-amber-800',
    label: 'Routemize Booking',
    highlight: true,
    category: 'routemize',
  },

  // Routemize form -> request fallback
  routemize_request: {
    icon: Route,
    tileBg: 'bg-amber-50',
    tileIcon: 'text-amber-700',
    pillBg: 'bg-amber-50',
    pillText: 'text-amber-800',
    label: 'Routemize Request',
    category: 'routemize',
  },

  // Customer requests
  new_request: {
    icon: FileText,
    tileBg: 'bg-blue-50',
    tileIcon: 'text-blue-600',
    pillBg: 'bg-blue-50',
    pillText: 'text-blue-700',
    label: 'New Request',
    category: 'requests',
  },

  // Appointment lifecycle
  new_appointment: {
    icon: CalendarPlus,
    tileBg: apptBg,
    tileIcon: apptIcon,
    pillBg: apptPillBg,
    pillText: apptPillText,
    label: 'New Appointment',
    category: 'appointments',
  },
  appointment_rescheduled: {
    icon: CalendarClock,
    tileBg: 'bg-orange-50',
    tileIcon: 'text-orange-600',
    pillBg: 'bg-orange-50',
    pillText: 'text-orange-700',
    label: 'Rescheduled',
    category: 'appointments',
  },
  appointment_cancelled: {
    icon: CalendarX,
    tileBg: 'bg-rose-50',
    tileIcon: 'text-rose-600',
    pillBg: 'bg-rose-50',
    pillText: 'text-rose-700',
    label: 'Cancelled',
    category: 'appointments',
  },
  instant_booking: {
    icon: Zap,
    tileBg: 'bg-emerald-50',
    tileIcon: 'text-emerald-600',
    pillBg: 'bg-emerald-50',
    pillText: 'text-emerald-700',
    label: 'Instant Booking',
    category: 'appointments',
  },
  assignment: {
    icon: UserPlus,
    tileBg: 'bg-blue-50',
    tileIcon: 'text-blue-600',
    pillBg: 'bg-blue-50',
    pillText: 'text-blue-700',
    label: 'Assignment',
    category: 'appointments',
  },
  reminder: {
    icon: Clock,
    tileBg: 'bg-amber-50',
    tileIcon: 'text-amber-600',
    pillBg: 'bg-amber-50',
    pillText: 'text-amber-700',
    label: 'Reminder',
    category: 'appointments',
  },
  completed: {
    icon: CheckCircle,
    tileBg: 'bg-emerald-50',
    tileIcon: 'text-emerald-600',
    pillBg: 'bg-emerald-50',
    pillText: 'text-emerald-700',
    label: 'Completed',
    category: 'appointments',
  },
  appointment: {
    icon: Calendar,
    tileBg: apptBg,
    tileIcon: apptIcon,
    pillBg: apptPillBg,
    pillText: apptPillText,
    label: 'Appointment',
    category: 'appointments',
  },
  scheduled: {
    icon: Calendar,
    tileBg: apptBg,
    tileIcon: apptIcon,
    pillBg: apptPillBg,
    pillText: apptPillText,
    label: 'Scheduled',
    category: 'appointments',
  },
  // AI voice agent took a phone message (backend `VoiceMessage`). Its own
  // 'messages' category (not 'system') so every recipient sees it, not only
  // host admins.
  voice_message: {
    icon: Phone,
    tileBg: 'bg-tone-violet-100',
    tileIcon: 'text-tone-violet-600',
    pillBg: 'bg-tone-violet-100',
    pillText: 'text-tone-violet-600',
    label: 'Phone Message',
    category: 'messages',
  },
  team_update: {
    icon: UserPlus,
    tileBg: 'bg-sky-50',
    tileIcon: 'text-sky-600',
    pillBg: 'bg-sky-50',
    pillText: 'text-sky-700',
    label: 'Team Update',
    category: 'system',
  },
  system: {
    icon: Settings,
    tileBg: 'bg-ink-100',
    tileIcon: 'text-ink-500',
    pillBg: 'bg-ink-100',
    pillText: 'text-ink-600',
    label: 'System',
    category: 'system',
  },
};

/**
 * Aliases — backend/legacy keys that should resolve to the same config as
 * their canonical entry above.
 */
const ALIAS_MAP: Record<string, string> = {
  routemizeappointment: 'routemize_appointment',
  routemizerequest: 'routemize_request',
  newrequest: 'new_request',
  request: 'new_request',
  newappointment: 'new_appointment',
  appointmentrescheduled: 'appointment_rescheduled',
  rescheduled: 'appointment_rescheduled',
  meetingrescheduled: 'appointment_rescheduled',
  appointmentcancelled: 'appointment_cancelled',
  cancelled: 'appointment_cancelled',
  canceled: 'appointment_cancelled',
  meetingcancelled: 'appointment_cancelled',
  meetingcanceled: 'appointment_cancelled',
  instantbooking: 'instant_booking',
  instant: 'instant_booking',
  assigned: 'assignment',
  newassignment: 'assignment',
  meetingscheduled: 'scheduled',
  meetingcompleted: 'completed',
  teamupdate: 'team_update',
  voicemessage: 'voice_message',
};

const FALLBACK_CONFIG: NotificationVisualConfig = {
  icon: Bell,
  tileBg: 'bg-ink-100',
  tileIcon: 'text-ink-500',
  pillBg: 'bg-ink-100',
  pillText: 'text-ink-600',
  label: 'Notification',
  category: 'system',
};

/**
 * Normalize a notification type to a lowercase string key. The field is
 * typed as `NotificationType` (string union), but the backend can send
 * numeric enum ordinals or null in edge cases — `String(...)` makes this
 * bulletproof against any shape so a single bad payload doesn't crash the
 * whole notifications screen.
 */
const normalizeType = (type: unknown): string => {
  if (type == null) return '';
  return String(type).toLowerCase();
};

export const getNotificationVisual = (type: unknown): NotificationVisualConfig => {
  const key = normalizeType(type);
  const resolved = CONFIG_BY_TYPE[key] || CONFIG_BY_TYPE[ALIAS_MAP[key]];
  return resolved || FALLBACK_CONFIG;
};

/**
 * Detect whether a notification originated from a Routemize booking flow.
 *
 * The backend signals this in a few different ways depending on the path the
 * booking took, so we check all of them:
 *
 *  - `metadata.isRoutemize === true` (set when the DTO's `variables` bag
 *    contains an `isRoutemize` flag from the booking flow)
 *  - the explicit `routemize_appointment` / `routemize_request` types
 *  - `actionUrl` that points at the `/routemize` public booking route
 */
const isRoutemizeOriginated = (
  n: Pick<Notification, 'type' | 'metadata' | 'actionUrl'>,
): boolean => {
  if (n.metadata?.isRoutemize === true) return true;
  const t = normalizeType(n.type);
  if (t === 'routemize_appointment' || t === 'routemizeappointment') return true;
  if (t === 'routemize_request' || t === 'routemizerequest') return true;
  if (n.actionUrl && /routemize/i.test(n.actionUrl)) return true;
  return false;
};

/**
 * Resolve the category that a notification belongs to for filter pill purposes.
 *
 * Accepts either a raw type string (legacy callers) or a full Notification so
 * we can promote appointments booked via Routemize into the Routemize bucket
 * even when the backend stamps them with a generic `new_appointment` type.
 */
export const getNotificationCategory = (
  input: unknown,
): NotificationCategory => {
  if (input && typeof input === 'object' && 'type' in (input as Record<string, unknown>)) {
    const n = input as Pick<Notification, 'type' | 'metadata' | 'actionUrl'>;
    if (isRoutemizeOriginated(n)) return 'routemize';
    return getNotificationVisual(n.type).category;
  }
  return getNotificationVisual(input).category;
};

export const isAppointmentType = (type: unknown): boolean => {
  const v = getNotificationVisual(type);
  return v.category === 'appointments' || v.label === 'Routemize Booking';
};

export const isRequestType = (type: unknown): boolean => {
  const cat = getNotificationVisual(type).category;
  return cat === 'requests' || cat === 'routemize';
};

/**
 * Resolve a frontend route for a given notification.
 * Returns an object with `to` (path) and `label` (CTA copy) or null.
 */
export interface NotificationLink {
  to: string;
  label: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** The id as a string when it names a real entity — not null, blank or Guid.Empty. */
const realEntityId = (value: unknown): string | null => {
  if (value == null) return null;
  const id = String(value).trim();
  return id && id !== EMPTY_GUID ? id : null;
};

export const getNotificationLink = (notification: Notification): NotificationLink | null => {
  const meta = notification.metadata;

  // Phone message: open the caller's contact when one matched. Otherwise fall
  // back to the voice agent's Messages tab — it can't deep-link to a single
  // message, so there's no finer target.
  if (normalizeType(notification.type) === 'voice_message') {
    const contactId = realEntityId(meta?.contactId);
    return contactId
      ? { to: `/contacts/${contactId}`, label: 'View contact' }
      : { to: '/voice-agent?tab=messages', label: 'View messages' };
  }

  if (isAppointmentType(notification.type) && meta?.appointmentId) {
    const dateParam = meta.appointmentDate
      ? meta.appointmentDate.split('T')[0]
      : notification.timestamp.toISOString().split('T')[0];
    return {
      to: `/bookings?date=${dateParam}&appointmentId=${meta.appointmentId}`,
      label: 'View appointment',
    };
  }

  if (isRequestType(notification.type) && meta?.requestId) {
    return {
      to: `/requests?requestId=${meta.requestId}`,
      label: 'View request',
    };
  }

  if (notification.actionUrl) {
    // Treat same-origin URLs as internal routes; keep external links external.
    return { to: notification.actionUrl, label: 'Open' };
  }

  if (isRequestType(notification.type)) return { to: '/requests', label: 'View requests' };
  if (isAppointmentType(notification.type)) return { to: '/bookings', label: 'View bookings' };
  return null;
};

export const isExternalUrl = (url: string): boolean => {
  return /^https?:\/\//i.test(url);
};

/**
 * Lightweight category descriptor for the top-of-page filter pills.
 */
export interface CategoryDescriptor {
  id: NotificationCategory;
  label: string;
  icon: LucideIcon;
  /**
   * Only show the pill when the inbox holds a notification in this category.
   * Used for categories most tenants never receive (phone messages need the
   * AI voice agent), so they don't get a permanently empty pill.
   */
  onlyWhenPresent?: boolean;
}

export const CATEGORIES: CategoryDescriptor[] = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'requests', label: 'Requests', icon: FileText },
  { id: 'appointments', label: 'Appointments', icon: Calendar },
  { id: 'messages', label: 'Messages', icon: Phone, onlyWhenPresent: true },
  { id: 'system', label: 'System', icon: Settings },
];

/**
 * Group notifications by smart date bucket: Today, Yesterday, This Week,
 * then Month Year for older items. Returns keys in insertion order.
 */
export const groupByDate = (notifications: Notification[]): Record<string, Notification[]> => {
  const groups: Record<string, Notification[]> = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekStart = new Date(today.getTime() - today.getDay() * 86400000);

  for (const notification of notifications) {
    const d = new Date(notification.timestamp);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let key: string;
    if (day.getTime() === today.getTime()) key = 'Today';
    else if (day.getTime() === yesterday.getTime()) key = 'Yesterday';
    else if (day >= weekStart) key = 'Earlier this week';
    else {
      key = d.toLocaleDateString('en-US', {
        month: 'long',
        year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
    if (!groups[key]) groups[key] = [];
    groups[key].push(notification);
  }
  return groups;
};
